import React from "react";
import { ShieldCheck, Users, Lock, LineChart, Globe, CheckCircle } from "lucide-react";

export default function MastaKashSaccoLanding() {
  return (
   <div className="container">
     <div className="min-h-screen bg-gray-50 text-gray-900">
      {/* Hero */}
      <section className="bg-gradient-to-r from-purple-700 to-orange-500 text-white py-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Rebuilding Trust in SACCOs — One System at a Time
          </h1>
          <p className="text-lg md:text-xl max-w-3xl mx-auto mb-8">
            Regulated digital infrastructure that protects member savings,
            enforces governance, and enables SACCOs to scale securely.
          </p>
          <button className="bg-white text-purple-700 font-semibold px-8 py-3 rounded-2xl shadow-lg hover:scale-105 transition">
            Register Interest
          </button>
        </div>
      </section>

      {/* Problem */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">The Problem</h2>
          <p className="text-gray-700 max-w-3xl mb-8">
            Too many SACCOs struggle with bad loans, mismanagement, weak
            governance, and outdated systems. When transparency fails,
            members lose trust — and SACCOs lose their future.
          </p>
          <ul className="grid md:grid-cols-2 gap-4 text-gray-800">
            <li>• Bad loans and defaults</li>
            <li>• Misuse of member savings</li>
            <li>• Weak governance and poor transparency</li>
            <li>• Manual systems and slow service</li>
          </ul>
        </div>
      </section>

      {/* Solution */}
      <section className="bg-white py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold mb-10">The MastaKash Solution</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Feature icon={<Lock />} title="Ring-Fenced Savings" desc="Member funds are protected in system-controlled wallets." />
            <Feature icon={<ShieldCheck />} title="Governance by Design" desc="Rules are enforced automatically, not manually." />
            <Feature icon={<LineChart />} title="Loan Risk Control" desc="Risk analysis and guarantor-backed lending reduce defaults." />
            <Feature icon={<CheckCircle />} title="Full Audit Trails" desc="Every action is logged, traceable, and accountable." />
            <Feature icon={<Users />} title="Member Transparency" desc="Clear, readable statements members can understand." />
            <Feature icon={<Lock />} title="Secure Approvals" desc="Multi-signatory workflows aligned with SACCO rules." />
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-16 px-6 bg-gray-100">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">Why SACCOs Choose MastaKash</h2>
          <div className="grid md:grid-cols-2 gap-6 text-gray-800">
            <p>✔ Reduce fraud and mismanagement</p>
            <p>✔ Improve loan recovery</p>
            <p>✔ Increase member confidence</p>
            <p>✔ Deliver faster digital services</p>
            <p>✔ Operate countrywide without branches</p>
            <p>✔ Stay aligned with regulators</p>
          </div>
        </div>
      </section>

      {/* Scale */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">Built for Scale. Designed for Compliance.</h2>
          <p className="text-gray-700 max-w-3xl">
            MastaKash enables SACCOs to recruit members nationwide, operate
            digitally, and maintain strong controls. Geography disappears —
            governance remains.
          </p>
        </div>
      </section>

      {/* Pre-launch */}
      <section className="bg-purple-700 text-white py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">Pre-Launch Participation</h2>
          <p className="max-w-3xl mb-6">
            MastaKash is currently in the licensing process. Selected SACCOs
            can participate in governance simulations, receive free training,
            and prepare for compliant digital operations — with no financial
            transactions enabled.
          </p>
          <ul className="space-y-2">
            <li>• No deposits</li>
            <li>• No payments</li>
            <li>• No obligations</li>
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 text-center">
        <h2 className="text-3xl font-bold mb-4">Prepare Your SACCO for the Future</h2>
        <p className="text-gray-700 mb-8">Strong governance is no longer optional.</p>
        <button className="bg-orange-500 text-white px-8 py-3 rounded-2xl shadow-lg hover:scale-105 transition">
          Contact MastaKash
        </button>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-8 px-6 text-center">
        <p className="font-semibold">MastaKash</p>
        <p className="text-sm">Protecting members. Empowering SACCOs. Restoring trust.</p>
      </footer>
    </div>

   </div>
  );
}

function Feature({ icon, title, desc }) {
  return (
    <div className="bg-gray-50 p-6 rounded-2xl shadow-sm">
      <div className="text-purple-700 mb-4">{icon}</div>
      <h3 className="font-semibold text-lg mb-2">{title}</h3>
      <p className="text-gray-600 text-sm">{desc}</p>
    </div>
  );
}